import { CancelToken } from "axios";
import { ITEMS_PER_STRAIN_PAGE } from "constants/strains";

import consumerApi from "api/services/consumer-api";
import { Coordinates } from "custom-types/Coordinates";
import { Strain } from "custom-types/Strain";
import { StrainAvailableFilters } from "custom-types/StrainList";
import { formatFilterKey, formatSortParam } from "utils/formatFilterKey";
import formatImgixUrl from "utils/image/formatImgixUrl";
import logError from "utils/logError";

import {
  MenuFiltersGroup,
  MenuFiltersToggle,
} from "components/MenuFilters/types/MenuFilters";

const DEFAULT_SORT = "recommended:asc";

type ApiResponse = {
  strains: Strain[];
  metadata: {
    totalCount: number;
    availableFilters:
      | (MenuFiltersGroup | MenuFiltersToggle)[]
      | StrainAvailableFilters;
    availableSorts: string[];
  };
  error?: boolean;
};

const getStrainLists = async (
  filters: { [key: string]: string | string[] | boolean | undefined },
  skip = 0,
  take = ITEMS_PER_STRAIN_PAGE,
  coordinates: Coordinates,
  sort?: string,
  cancelToken?: CancelToken,
  pageCategory?: string | string[] | undefined,
  pageName?: string | string[] | undefined
): Promise<ApiResponse> => {
  const { enableNewFilters = false, ...otherFilters } = filters;
  const filterParams = Object.entries(otherFilters).reduce((acc, curr) => {
    const [key, value] = curr;

    if (Array.isArray(value)) {
      value.forEach((element, index) => {
        const formattedFilter = formatFilterKey(
          key,
          element,
          enableNewFilters as boolean,
          index
        );
        acc = { ...acc, ...formattedFilter };
      });
    } else {
      const filterValue = otherFilters[key];
      const formattedFilter = formatFilterKey(
        key,
        filterValue as string,
        enableNewFilters as boolean,
        0
      );
      acc = { ...acc, ...formattedFilter };
    }

    return acc;
  }, {});

  const formattedSortParam = formatSortParam(sort ? sort : DEFAULT_SORT);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  const params: any = {
    enableNewFilters,
    skip,
    strain_playlist: pageCategory === "curated" ? pageName : "",
    take,
    ...(pageCategory === "new-strains"
      ? { "filter[is_new_strain]": true }
      : {}),
    ...coordinates,
    ...filterParams,
    ...(sort === "rating:desc"
      ? { [`sort[1][review_count]`]: "desc", ...formattedSortParam }
      : formattedSortParam),
  };

  if (pageCategory !== "curated" && pageCategory !== undefined) {
    params["attribute_type"] = pageCategory;
    params["attribute_name"] = pageName;
  }

  try {
    const result = await consumerApi.get(`/api/strain_playlists/v2`, {
      cancelToken,
      params,
    });

    const {
      data: {
        hits: { strain },
        metadata,
      },
    } = result;

    strain?.length &&
      strain.forEach((strain: Strain) => {
        if (strain.nugImage) {
          strain.nugImage = formatImgixUrl(strain.nugImage);
        }
      });

    return { metadata, strains: (strain as Strain[]) || [] };
  } catch (e) {
    if (e.statusCode !== "ERR_CANCELED") {
      logError(e.message, {
        functionName: "getStrainLists",
        service: "consumer",
        statusCode: e.statusCode,
      });
    }

    return {
      metadata: { availableFilters: [], availableSorts: [], totalCount: 0 },
      strains: [],
    };
  }
};

export default getStrainLists;
